import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useAxios, apiEndpoints } from 'api';
import { AutoAlertStatus } from 'components';
import { UserEmployeeInfo } from 'api/types';
import { NotificationDeadline } from 'api/types/UserEmployeeInfo';
import { QualificationsAdditionalParam } from 'api/types/Qualifications';

const useGetUserEmployeeInfo = (
  onError?: (error: AxiosError<Error>) => void
): [
  UserEmployeeInfo[] | null | undefined,
  (data: UserEmployeeInfo[]) => void
] => {
  const ax = useAxios();

  const [dataState, setDataState] = useState<
    UserEmployeeInfo[] | null | undefined
  >(undefined);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await ax.get<UserEmployeeInfo[]>(
        `${apiEndpoints.employeeKiosk}/user`
      );
      setDataState(data);
      return data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status !== undefined) {
          AutoAlertStatus({
            status: error.response?.status,
            customMsg: JSON.parse(error.response?.request.response).message,
          });
        }
      }
      if (axios.isAxiosError(error)) {
        onError?.(error as AxiosError<Error>);
      }
      setDataState(null);
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ax, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateData = useCallback((data: UserEmployeeInfo[]) => {
    setDataState(data);
  }, []);

  return [dataState, updateData];
};

const useGetNotificationDeadline = (
  onCompleted?: (data: NotificationDeadline[] | null | undefined) => void,
  onError?: (error: AxiosError<Error>) => void
): ((additionalParam: QualificationsAdditionalParam) => Promise<void>) => {
  const ax = useAxios();

  const postItem = useCallback(
    async (additionalParam: QualificationsAdditionalParam) => {
      try {
        const rData = await ax.get<NotificationDeadline[] | null>(
          `${apiEndpoints.employeeKiosk}/user/${additionalParam.userId}/notification-deadline`
        );

        if (onCompleted) onCompleted(rData.data);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            AutoAlertStatus({
              status: error.response.status,
              customMsg: JSON.parse(error.response?.request?.response).message,
            });
          }
          // ✅ onError powinno się wykonać także gdy nie ma response
          onError?.(error);
        } else {
          // ✅ gdy to nie axios error, a ktoś chce dostać błąd:
          // nie pasuje typem do AxiosError, więc tylko log / rzuć dalej
          // console.error(error);
        }
      }
    },
    [ax, onCompleted, onError]
  );

  return postItem;
};

export default {
  useGetUserEmployeeInfo,
  useGetNotificationDeadline,
};
